/**
 * WHILE-YOU'RE-HERE RULE
 * ----------------------
 * When you touch a file that has `toast.error('Failed to X')`, replace it with:
 *     toast.error(getApiErrorMessage(error, 'Failed to X'))
 * Don't bulk-sweep — just migrate handlers in files you're already editing
 * for other reasons. Real backend messages save real diagnostic time.
 */

/**
 * Extract a user-actionable message from an axios/fetch error.
 *
 * Order of preference:
 *   1. response.data.detail        — FastAPI's standard validation/error format
 *   2. response.data.message       — common alternate API convention
 *   3. response.data.error         — another alternate convention
 *   4. response.statusText         — e.g., "Internal Server Error"
 *   5. error.message               — generic JS error
 *   6. fallback                    — caller-provided default
 *
 * If response.data.detail is a Pydantic validation array (list of objects with
 * {loc, msg, type}), the first error is formatted as "field — msg" for
 * readability. Otherwise the value is returned as-is (string).
 *
 * @param {Error} error - The error caught from an axios/fetch call
 * @param {string} fallback - Message to show if no useful info found (e.g., "Failed to save task")
 * @returns {string} A user-displayable error message
 */
export function getApiErrorMessage(error, fallback = 'Something went wrong') {
  if (!error) return fallback;

  const responseData = error?.response?.data;

  if (responseData) {
    // FastAPI's standard error key
    if (responseData.detail) {
      const detail = responseData.detail;

      // Pydantic validation: detail is an array of {loc, msg, type, ...}
      if (Array.isArray(detail) && detail.length > 0) {
        const first = detail[0];
        if (first?.loc && first?.msg) {
          // Format the field path like "field" or "parent.child"
          const field = Array.isArray(first.loc)
            ? first.loc.filter(p => p !== 'body').join('.')
            : String(first.loc);
          return field ? `${field} — ${first.msg}` : first.msg;
        }
        if (first?.msg) return first.msg;
      }

      // Plain string detail
      if (typeof detail === 'string') return detail;
    }

    if (typeof responseData.message === 'string') return responseData.message;
    if (typeof responseData.error === 'string') return responseData.error;
  }

  if (error?.message === 'Network Error') {
    return 'Network error — check your connection';
  }

  if (error?.response?.statusText) {
    return `${fallback} (${error.response.statusText})`;
  }

  if (error?.message) {
    return `${fallback} (${error.message})`;
  }

  return fallback;
}

/**
 * Convenience wrapper for the common pattern:
 *   catch (e) { toast.error(getApiErrorMessage(e, 'Failed to X')) }
 *
 * Usage:
 *   import { toast } from 'sonner';
 *   import { toastApiError } from '@/utils/apiError';
 *
 *   } catch (error) {
 *     toastApiError(toast, error, 'Failed to save task');
 *   }
 */
export function toastApiError(toastFn, error, fallback) {
  if (!toastFn?.error) return;
  toastFn.error(getApiErrorMessage(error, fallback));
}
